"use client";

import { useEffect, useRef, useState } from "react";

/*
 * Scroll reveal.
 *
 * THE FAILSAFE IS NOT OPTIONAL. The handoff is explicit: content must never be
 * permanently invisible if the observer fails. So a 3-second timer reveals
 * everything regardless, and `prefers-reduced-motion` skips the animation
 * entirely rather than shortening it.
 *
 * A marketing page that renders blank because an IntersectionObserver threw is
 * a page that sells nothing, and it fails silently in exactly the browsers you
 * do not test.
 */
export function Reveal({
  children, delay = 0, as: Tag = "div",
}: {
  children: React.ReactNode;
  delay?: number;
  as?: "div" | "section" | "li";
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    // The failsafe, armed before the observer so it survives the observer
    // throwing on construction.
    const failsafe = setTimeout(() => setVisible(true), 3000);

    const el = ref.current;
    if (!el) { setVisible(true); return; }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setTimeout(() => setVisible(true), delay);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.05 },
    );
    io.observe(el);

    return () => { clearTimeout(failsafe); io.disconnect(); };
  }, [delay]);

  return (
    <Tag
      ref={ref as never}
      className={`reveal${visible ? " is-visible" : ""}`}
    >
      {children}
    </Tag>
  );
}
